import { AbstractControl, FormGroup, Validators } from '@angular/forms';

import { TGANumberQuestion, TGAQuestion, TGATableQuestion, TgaInputType } from '../models/question-models';

export const updateQuestionAfterVisibilityUpdate = (
  question: TGAQuestion<TgaInputType>,
  formGroup: FormGroup,
): void => {
  const formQuestion = formGroup.get(question.key);
  if (!formQuestion) return;

  if (!question.isVisible) {
    if (formQuestion.value !== null) {
      formQuestion.setValue(null, { emitEvent: false });
    }
    formQuestion.setValidators([]);
    formQuestion.updateValueAndValidity({ emitEvent: false });
    return;
  }

  formQuestion.clearValidators();
  addQuestionValidators(question, formQuestion);
  formQuestion.updateValueAndValidity({ emitEvent: false });
};

export const addQuestionValidators = (question: TGAQuestion<TgaInputType>, formQuestion: AbstractControl) => {
  if (question.required) {
    formQuestion.addValidators(Validators.required);
  }

  if (question.controlType === TGANumberQuestion.numberControlType) {
    const numQuestion = question as TGANumberQuestion;

    if (numQuestion.range) {
      formQuestion.addValidators(Validators.min(numQuestion.range.min));
      formQuestion.addValidators(Validators.max(numQuestion.range.max));
    }
  }
};

export const updateVisibilityOnDependantQuestions = (questions: TGAQuestion<TgaInputType>[], formGroup: FormGroup) => {
  const flattenedQuestions = flattenQuestions(questions);

  const dependantQuestions = flattenedQuestions.filter((qa) => qa.dependsOnQuestion);
  const dependantQuestionKeys = new Set(dependantQuestions.map((dq) => dq.dependsOnQuestion?.questionKey));
  const rootParents = flattenedQuestions.filter((qa) => dependantQuestionKeys.has(qa.key) && !qa.dependsOnQuestion);
  resolveDependantQuestions(dependantQuestions, rootParents, formGroup);
};

export const updateDisabledQuestions = (questions: TGAQuestion<TgaInputType>[], formGroup: FormGroup) => {
  const flattenedQuestions = flattenQuestions(questions);

  for (const question of flattenedQuestions) {
    const control = formGroup.get(question.key);
    if (control) {
      if (question.isDisabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }
};

const resolveDependantQuestions = (
  dependantQuestions: TGAQuestion<TgaInputType>[],
  parents: TGAQuestion<TgaInputType>[],
  formGroup: FormGroup,
) => {
  const questionsDependOnParents = dependantQuestions.filter((qa) =>
    parents.map((p) => p.key).includes(qa.dependsOnQuestion?.questionKey ?? ''),
  );

  updateVisibility(questionsDependOnParents, parents, formGroup);

  const childQuestions = dependantQuestions.filter((qa) => !questionsDependOnParents.includes(qa));
  if (childQuestions.length) {
    resolveDependantQuestions(childQuestions, questionsDependOnParents, formGroup);
  }
};

const updateVisibility = (
  questions: TGAQuestion<TgaInputType>[],
  parents: TGAQuestion<TgaInputType>[],
  formGroup: FormGroup,
) => {
  for (const question of questions) {
    const parent = parents.find((qa) => qa.key === question.dependsOnQuestion?.questionKey);
    const parentValue = formGroup ? formGroup.get(parent?.key ?? '')?.value : parent?.value;

    if ((parent?.dependsOnQuestion && !parent?.isVisible) || !parentValue) {
      question.isVisible = false;
    } else {
      question.isVisible = question.dependsOnQuestion?.questionVisibleForValues.includes(parentValue as string);
    }
    if (formGroup) {
      updateQuestionAfterVisibilityUpdate(question, formGroup);
    }
  }
};

const flattenQuestions = (questions: TGAQuestion<TgaInputType>[]) => {
  // Flatten questions to include sub-questions from table questions
  const flattenedQuestions: TGAQuestion<TgaInputType>[] = [];

  for (const question of questions) {
    flattenedQuestions.push(question);
    if (question.controlType === 'table') {
      const tableQuestion = question as TGATableQuestion;
      flattenedQuestions.push(...tableQuestion.subQuestions);
    }
  }

  return flattenedQuestions;
}
